/**
 * Home Route - User dashboard (authenticated)
 */

// Import web components from shared directory
import '../../../../shared/wc/m5t-primary-nav.js';
import '../../../../shared/wc/m5t-chat-prompt.js';
import '../../../../shared/wc/m5t-chat-prompt-area.js';
import { setMarkdownLoader } from '../../../../shared/wc/m5t-chat-response.js';
import '../../../../shared/wc/m5t-chat-workflow.js';
import '../../../../shared/wc/m5t-chat-waiting.js';
import { NavMetaManager } from '../../../../shared/meta/meta_nav.js';
import { setQueryEngine } from '../../../../shared/meta/meta_templates.js';
import { appsContext } from '../services/appsCache.js';
import '../templates/index.js';

// Hand the response bubble our bundled markdown-it. The component cannot import
// it directly - shared/ is outside the pnpm workspaces - and used to fetch it
// from a CDN at runtime. Kicked off here, well before the first bubble exists.
setMarkdownLoader(async () => {
  const { default: MarkdownIt } = await import('markdown-it');
  const md = new MarkdownIt({
    html: false,        // no raw HTML from model output
    breaks: true,       // \n -> <br>
    linkify: true,
    typographer: true
  });
  return (text) => md.render(text);
});

// Hand the template registry our bundled JMESPath, for the same reason - it is
// what resolves a template's `source` expression against a fetched payload.
import('@metrichor/jmespath')
  .then(({ search }) => setQueryEngine((expression, data) => search(data, expression)))
  .catch((err) => console.warn('[HomeRoute] JMESPath unavailable - source falls back to dot paths', err));

// The registry is still the back-channel, but it feeds the ROUTING pass now, not
// the chat prompt - see routingPrompt() in meta_templates.js. The catalog used to
// be registered here as a SystemPromptProvider contributor; it was removed because
// the prose model cannot render a view (only the router can reach one), so listing
// them there only invited it to promise something it does not control.

// chat() only ever runs when the routing pass declined, so at this point the model
// genuinely has no way to act - and it does not know that. Asked to "create a new
// app named Scott", it replied "I have created a new app named Scott" and no record
// existed. A confident false confirmation about a mutation is worse than any
// refusal, so the one thing the prose path must be told is what it cannot do.
// A living view of this user's apps. Populated by the MY_APPS fetch and after any
// action that changes the list - see services/appsCache.js. Contributes nothing
// until something has actually loaded, so the model never asserts an empty
// account to someone who has four.
//
// chat() only. routingPrompt() stays a pure function of the registry.
SystemPromptProvider.register('apps', () => appsContext());

// chat() is now reached almost only via EXPLAIN - the router sends out-of-scope
// requests straight to a decline without a generation. So this prompt can assume
// the question is about the product, and say what "answering" means here.
SystemPromptProvider.register('scope', () => [
  'You answer questions about StreamSyncEngage, a SaaS platform where the user registers apps,',
  'verifies their domains, and installs a snippet on their site. Answer only about this product',
  'and this account. If a question is not about it, say so briefly rather than answering.'
].join(' '));

SystemPromptProvider.register('capabilities', () => [
  'You cannot create, change, delete or fetch anything. You have no tools and no access',
  'to the user\'s data. If the user asks you to perform an action, tell them plainly that',
  'you were not able to do it and suggest they rephrase the request.',
  'Never state or imply that you have done something. Do not describe an action as complete.'
].join(' '));

// Import PubSub for event-driven architecture
import PubSub from 'pubsub-js';
import AppBroadcast from '../../../../shared/appBroadcast.js';
import { createTurn, ROLE, SOURCE, PHASE, SystemPromptProvider } from '../../../../shared/conversation.js';

// The model-download progress line addresses one bubble for its whole lifetime.
const LLM_STATUS_BUBBLE_ID = 'llm-status';

// Set app-specific navigation context
NavMetaManager.setAppToken('client-saas');

export function render() {
  return `
    <div class="home-container">
      <m5t-primary-nav
        data-buttons='${NavMetaManager.getPrimaryNavJSON()}'
      ></m5t-primary-nav>

      <main class="home-main" id="chat-canvas">
        <!-- Chat bubbles will be injected here dynamically -->
        <div class="chat-messages" id="chat-messages">
          <!-- Messages will appear here via PubSub events -->
        </div>
      </main>

      <!-- Fixed Prompt Area -->
      <div class="prompt-area" id="prompt-area">
        <m5t-chat-prompt-area placeholder="Ask me anything..."></m5t-chat-prompt-area>
      </div>
    </div>

    <style>
      :root {
        overflow: hidden !important;
        scrollbar-gutter: auto !important;
      }

      * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
      }

      html {
        overflow: hidden !important;
        height: 100vh;
        width: 100vw;
        scrollbar-gutter: auto !important;
        scrollbar-width: none !important;
      }

      body {
        overflow: hidden !important;
        height: 100vh;
        width: 100vw;
        margin: 0 !important;
        padding: 0 !important;
        scrollbar-gutter: auto !important;
        scrollbar-width: none !important;
      }

      #app {
        overflow: hidden !important;
        height: 100vh;
        scrollbar-gutter: auto !important;
      }

      .home-container {
        height: 100vh;
        width: 100vw;
        background: #6c757d;
        padding: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
        overflow: hidden !important;
        position: relative;
        scrollbar-gutter: auto !important;
      }

      /* Chat Canvas - Scrollable Area */
      .home-main {
        flex: 1;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        padding: 0;
        margin: 0;
        scrollbar-gutter: auto !important;
      }

      .chat-messages { 
        /* max-width: 900px; */
        /* margin: 0 auto; */
        padding: 32px 32px 100px 32px;
        display: flex;
        flex-direction: column-reverse;
        gap: 1rem;
        overflow-y: auto;
        flex: 1;
        width: 100%;
      }

      /* Fixed Prompt Area */
      .prompt-area {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        min-height: 80px;
        background: #1a1a1a;
        border-top: 2px solid #3a3a3a;
        z-index: 100;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 1rem;
        box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.3);
      }

      /* Responsive Design */
      @media (max-width: 768px) {
        .chat-messages {
          padding: 1rem 0.75rem;
        }

        .chat-bubble {
          max-width: 85%;
        }

        .bubble-content {
          padding: 0.75rem 1rem;
          font-size: 0.9rem;
        }

        .prompt-area {
          min-height: 70px;
          padding: 0.75rem;
        }
      }

      /* Custom Scrollbar - Applied to chat-messages (the actual scrolling container) */
      .chat-messages::-webkit-scrollbar {
        width: 8px;
      }

      .chat-messages::-webkit-scrollbar-track {
        background: #2d2d2d;
      }

      .chat-messages::-webkit-scrollbar-thumb {
        background: #92a8d1;
        border-radius: 4px;
      }

      .chat-messages::-webkit-scrollbar-thumb:hover {
        background: #364C75;
      }
    </style>
  `;
}

export function init() {
  console.log('[HomeRoute] Initialized');

  // Navigation events are now handled globally by the WebComponentBridge in app_events.js
  // The bridge listens to all 'nav-select' events and publishes to M5T_PRIMARY_NAV_TAP
  // Route-specific logic is handled by PubSub subscribers in app_events.js

  // No scroll manipulation needed - column-reverse handles bottom-anchoring automatically!

  // CONVERSATION events are handled globally in app_events.js
  // Canvas population happens via PubSub -> app_events.js -> addChatBubbleToCanvas()

  // Subscribe to CANVAS.DO.SCROLL event
  PubSub.subscribe(AppBroadcast.CANVAS_DO_SCROLL, (msg, data) => {
    console.log('[HomeRoute] CANVAS.DO.SCROLL triggered');
    scrollToBottomOfCanvas();
  });

  // Subscribe to LLM_STATUS for download progress
  PubSub.subscribe(AppBroadcast.LLM_STATUS, (msg, data) => {
    console.log('[HomeRoute] LLM_STATUS:', data);

    if (data && data.text) {
      // Fixed id: progress owns one bubble for the whole download and updates
      // it in place. Without an id of its own it used to capture whichever
      // response bubble happened to be first in the canvas.
      PubSub.publish(AppBroadcast.CONVERSATION_RESPONSE_CANVAS, createTurn({
        id: LLM_STATUS_BUBBLE_ID,
        turnId: LLM_STATUS_BUBBLE_ID,
        role: ROLE.ASSISTANT,
        source: SOURCE.SYSTEM,
        text: `🔄 ${data.text}`,
        phase: PHASE.DELTA,
        meta: { progress: data.progressPercent }
      }));
    }
  });
}

/**
 * Smoothly scroll to bottom of chat canvas
 * Uses 80ms delay for smooth animation
 *
 * Note: With column-reverse, scrolling to 0 shows the latest messages at the bottom
 */
function scrollToBottomOfCanvas() {
  setTimeout(() => {
    const chatMessages = document.getElementById('chat-messages');
    if (chatMessages) {
      // With column-reverse, scroll to 0 to show latest messages
      chatMessages.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      console.log('[HomeRoute] Scrolled chat-messages to bottom (top: 0 with column-reverse)');
    } else {
      console.warn('[HomeRoute] chat-messages element not found');
    }
  }, 80);
}

/**
 * Add chat bubble via PubSub (public API)
 * Publishes to CONVERSATION topics which are handled by subscribers
 *
 * @param {string} message - Message text
 * @param {string} type - 'user' or 'assistant'
 * @param {string} target - Where to send: 'canvas', 'agent', 'agent_canvas', 'stage'
 */
function addChatBubble(message, type = 'user', target = 'canvas') {
  const timestamp = new Date().toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit'
  });

  const data = { message, timestamp };

  if (type === 'user' || type === 'prompt') {
    // Publish user prompt
    switch(target.toUpperCase()) {
      case 'CANVAS':
        PubSub.publish(AppBroadcast.CONVERSATION_PROMPT_CANVAS, data);
        break;
      case 'AGENT':
        PubSub.publish(AppBroadcast.CONVERSATION_PROMPT_AGENT, data);
        break;
      case 'AGENT_CANVAS':
        PubSub.publish(AppBroadcast.CONVERSATION_PROMPT_AGENT_CANVAS, data);
        break;
      case 'STAGE':
        PubSub.publish(AppBroadcast.CONVERSATION_PROMPT_STAGE, data);
        break;
      default:
        PubSub.publish(AppBroadcast.CONVERSATION_PROMPT_CANVAS, data);
    }
  } else {
    // Publish assistant response
    switch(target.toUpperCase()) {
      case 'CANVAS':
        PubSub.publish(AppBroadcast.CONVERSATION_RESPONSE_CANVAS, data);
        break;
      case 'WORKFLOW':
        PubSub.publish(AppBroadcast.CONVERSATION_RESPONSE_WORKFLOW, data);
        break;
      default:
        PubSub.publish(AppBroadcast.CONVERSATION_RESPONSE_CANVAS, data);
    }
  }
}

// ============================================
// DISPOSABLE TEST FUNCTIONS (Remove later)
// ============================================

/**
 * Generate random chat bubbles for testing scroll behavior
 * Call from console: window.generateRandomBubbles(20)
 *
 * @param {number} count - Number of bubbles to generate
 * @param {number} delay - Delay between bubbles in ms (default: 500)
 */
function generateRandomBubbles(count = 10, delay = 500) {
  const userMessages = [
    "Hey, how's it going?",
    "Can you help me with something?",
    "What's the weather like today?",
    "I need assistance with my account",
    "Tell me a joke!",
    "How do I reset my password?",
    "What are your hours?",
    "Thanks for your help!",
    "I have a question about billing",
    "Can you explain this feature?",
    "How do I upgrade my plan?",
    "What's new in the latest update?",
    "I'm having trouble logging in",
    "Where can I find the settings?",
    "How secure is my data?",
    "Do you offer customer support?",
    "What payment methods do you accept?",
    "Can I cancel my subscription?",
    "How do I export my data?",
    "Is there a mobile app?"
  ];

  const assistantMessages = [
    "I'm doing great! How can I help you today?",
    "Of course! I'd be happy to assist you.",
    "Let me check that for you...",
    "Here's what I found:",
    "That's a great question!",
    "I can help you with that.",
    "Let me walk you through the steps.",
    "You're very welcome!",
    "I understand your concern. Let me explain...",
    "That feature is located in the settings menu.",
    "Your account is fully secured with encryption.",
    "We're available 24/7 to help you!",
    "We accept all major credit cards and PayPal.",
    "You can upgrade anytime from your account settings.",
    "The latest update includes several new features!",
    "Try resetting your password using the forgot password link.",
    "Your data is encrypted and stored securely.",
    "Yes! Our mobile app is available for iOS and Android.",
    "You can export your data from the account settings.",
    "Absolutely! You can cancel anytime with no fees."
  ];

  let counter = 0;

  const interval = setInterval(() => {
    if (counter >= count) {
      clearInterval(interval);
      console.log(`[Test] Generated ${count} random bubbles`);
      return;
    }

    // Randomly choose user or assistant
    const isUser = Math.random() > 0.5;
    const type = isUser ? 'user' : 'assistant';

    // Pick random message
    const messages = isUser ? userMessages : assistantMessages;
    const message = messages[Math.floor(Math.random() * messages.length)];

    // Add bubble
    addChatBubble(message, type);

    counter++;
    console.log(`[Test] Added bubble ${counter}/${count} (${type})`);
  }, delay);
}

/**
 * Continuous bubble generation (runs until stopped)
 * Call from console: window.startBubbleStream()
 * Stop with: window.stopBubbleStream()
 */
let bubbleStreamInterval = null;

function startBubbleStream(delayMs = 1000) {
  if (bubbleStreamInterval) {
    console.log('[Test] Bubble stream already running. Stop it first with stopBubbleStream()');
    return;
  }

  console.log('[Test] Starting continuous bubble stream...');

  bubbleStreamInterval = setInterval(() => {
    const isUser = Math.random() > 0.5;
    const messages = isUser
      ? ["Quick message!", "Another one!", "Test bubble", "Hello there!", "Random user text"]
      : ["Response here", "Got it!", "Processing...", "Here's the answer", "Understood!"];

    const message = messages[Math.floor(Math.random() * messages.length)];
    addChatBubble(message, isUser ? 'user' : 'assistant');
  }, delayMs);
}

function stopBubbleStream() {
  if (bubbleStreamInterval) {
    clearInterval(bubbleStreamInterval);
    bubbleStreamInterval = null;
    console.log('[Test] Bubble stream stopped');
  } else {
    console.log('[Test] No bubble stream running');
  }
}

/**
 * Clear all chat bubbles
 */
function clearAllBubbles() {
  const chatMessages = document.getElementById('chat-messages');
  if (chatMessages) {
    chatMessages.innerHTML = '';
    console.log('[Test] All bubbles cleared');
  }
}

// Expose test functions globally
window.addChatBubble = addChatBubble;
window.generateRandomBubbles = generateRandomBubbles;
window.startBubbleStream = startBubbleStream;
window.stopBubbleStream = stopBubbleStream;
window.clearAllBubbles = clearAllBubbles;

// Log available test functions
console.log('[HomeRoute] Test functions available:');
console.log('  - window.generateRandomBubbles(count, delay) - Generate N bubbles with delay');
console.log('  - window.startBubbleStream(delayMs) - Continuous bubble generation');
console.log('  - window.stopBubbleStream() - Stop continuous generation');
console.log('  - window.clearAllBubbles() - Remove all bubbles');
console.log('  - window.addChatBubble(message, type) - Add single bubble');
